import { type CustomFormApi, Form } from 'informed';
import type { LibError, PlayerFieldNames, Results } from 'payments-lib-types';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { InputDate } from '@/components/form/Date';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Input } from '@/components/form/Input';
import { InputPhoneNumber } from '@/components/form/InputPhoneNumber';
import { camelize, snakify } from '@/helpers/common';
import type { ThemeMode } from '@/helpers/enums';
import { savePlayerFields } from '@/helpers/payments';
import { showTooltip } from '@/helpers/tooltip';
import { validateDate } from '@/helpers/validators';
import { useSwrMutation } from '@/hooks/swr';
import { PersistentFormTracker } from '@/modules/forms/components/PersistentFormTracker';
import {
  type PaymentMethod as PaymentMethodI,
  useGetCommonStore,
} from '@/store';
import { usePersistentFormsSlice } from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { TooltipTypes } from '@/store/tooltipStore';
import {
  APPEARANCE_FIELDS,
  type IProfileInfoFormData,
  type ProfileInfoConfig,
  TEXT_FIELDS,
} from './config';
import {
  formatProfileInfoFormValues,
  getValidProfileInfoValues,
  persistProfileInfoForm,
} from './helpers';
import { ProfileInfoStatesField } from './ProfileInfoStatesField';
import { ScProfileInfoForm, ScSecurityMessage } from './styled';

const INLINE_ERROR_FIELDS = new Set<string>([
  ...Object.values(TEXT_FIELDS)
    .filter((field): field is NonNullable<typeof field> => Boolean(field))
    .map((field) => field.name),
  'state',
]);

interface IProfileInfoFormProps {
  methodFields: Results.GetMethodFieldsResult;
  paymentMethod: PaymentMethodI | null;
  mode?: ThemeMode;
  config: ProfileInfoConfig;
  country?: string;
  onSuccess: () => void;
}

export const ProfileInfoForm: FC<IProfileInfoFormProps> = ({
  methodFields,
  paymentMethod,
  mode,
  config,
  country,
  onSuccess,
}) => {
  const { t } = useTranslation(['common', 'validation', 'deposits']);

  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const countryCode = useCommonStore.use.countryCode();
  const fetchUser = useCommonStore.use.fetchUser();

  const formApiRef = useRef<CustomFormApi<IProfileInfoFormData>>(null);
  const hasSentAppearanceEvent = useRef(false);

  const setPersistentFormData = usePersistentFormsSlice(
    (s) => s.setPersistentFormData
  );
  const getPersistentFormData = usePersistentFormsSlice(
    (s) => s.getPersistentFormData
  );

  const missingFields = useMemo(() => {
    const fields: Partial<Record<PlayerFieldNames, boolean>> = {};

    methodFields.playerFields.forEach(({ field }) => {
      fields[field] = true;
    });

    return fields;
  }, [methodFields]);

  const initialValues = useMemo(() => {
    const persistentValues = getPersistentFormData(config.persistFormName);
    const requiredKeys = new Set(Object.keys(camelize(missingFields)));

    const filteredValues = Object.fromEntries(
      Object.entries(persistentValues).filter(([key]) => requiredKeys.has(key))
    ) as IProfileInfoFormData;

    return getValidProfileInfoValues(filteredValues, countryCode);
  }, [
    missingFields,
    getPersistentFormData,
    config.persistFormName,
    countryCode,
  ]);

  const { trigger: requestSavePlayerFields, isMutating: saveFieldsPending } =
    useSwrMutation({
      key: config.saveKey,
      fetcher: savePlayerFields,
      options: {
        onError: (error: LibError.SavePlayerFieldsError | string) => {
          config.sendResultEvent({
            status: 'failed',
            error_data: JSON.stringify(error),
          });

          if (typeof error === 'string') {
            persistProfileInfoForm(
              formApiRef,
              setPersistentFormData,
              config.persistFormName
            );
            return;
          }

          if (error.playerFieldsErrors) {
            const fieldErrors = camelize(error.playerFieldsErrors);

            Object.entries(fieldErrors).forEach(([field, messages]) => {
              formApiRef.current?.setError(field, messages[0] || '');
            });

            persistProfileInfoForm(
              formApiRef,
              setPersistentFormData,
              config.persistFormName,
              Object.keys(fieldErrors)
            );

            const hasHiddenError = Object.keys(fieldErrors).some(
              (field) => !INLINE_ERROR_FIELDS.has(field)
            );
            if (hasHiddenError) {
              showTooltip({
                type: TooltipTypes.ERROR,
                body: t('common:errorUnknown'),
              });
            }
            return;
          }

          showTooltip({
            type: TooltipTypes.ERROR,
            body: t('common:errorUnknown'),
          });
        },
        onSuccess: async () => {
          config.sendResultEvent({ status: 'success' });
          await fetchUser(config.fetchUserReason);
          onSuccess();
        },
        populateCache: true,
      },
    });

  const handleSubmit = (values: IProfileInfoFormData) => {
    const formattedValues = formatProfileInfoFormValues(values);
    requestSavePlayerFields({
      id: paymentMethod?.id,
      currency: user?.currency,
      paymentAction: config.paymentAction,
      playerFieldsData: snakify(formattedValues),
      ...(country != null ? { country } : {}),
    }).catch(() => null);
  };

  useEffect(() => {
    if (hasSentAppearanceEvent.current) return;
    hasSentAppearanceEvent.current = true;

    config.sendAppearanceEvent({
      payment_method: {
        brand: paymentMethod?.brand ?? '',
        provider: paymentMethod?.provider ?? '',
        id: paymentMethod?.id ?? '',
        type: paymentMethod?.type ?? '',
      },
      fields: APPEARANCE_FIELDS.filter(([key]) => missingFields[key]).map(
        ([, token]) => token
      ),
    });
  }, []);

  const renderTextField = (field: PlayerFieldNames) => {
    const fieldConfig = TEXT_FIELDS[field];
    if (!(fieldConfig && missingFields[field])) return null;

    const { name, labelKey, validate } = fieldConfig;
    const label = t(labelKey);

    return (
      <FormGroup key={field} mode={mode} title={label}>
        <Input
          mode={mode}
          name={name}
          placeholder={label}
          validate={validate}
          validateOn="change-blur"
        />
      </FormGroup>
    );
  };

  return (
    <ScProfileInfoForm>
      <Form<IProfileInfoFormData>
        className="form"
        formApiRef={formApiRef}
        initialValues={initialValues}
        onSubmit={({ values }) => handleSubmit(values)}
        onSubmitFailure={({ errors }) =>
          config.sendResultEvent({
            status: 'failed',
            error_data: JSON.stringify(errors),
          })
        }
      >
        <PersistentFormTracker formName={config.persistFormName} />
        <div className="rowGroup">
          {renderTextField('first_name')}
          {renderTextField('last_name')}
        </div>
        {missingFields.date_of_birth && (
          <FormGroup
            mode={mode}
            title={t('auth.registration.form.dateOfBirth')}
          >
            <InputDate
              mode={mode}
              name="dateOfBirth"
              validate={validateDate(countryCode)}
            />
          </FormGroup>
        )}
        {missingFields.mobile_phone && (
          <FormGroup
            mode={mode}
            title={t('auth.registration.form.phoneNumber')}
          >
            <InputPhoneNumber
              initialCodeValue={initialValues.mobilePhone?.code || countryCode}
              mode={mode}
              name="mobilePhone"
              validateOnBlur
              withValidate
            />
          </FormGroup>
        )}
        {missingFields.state && (
          <FormGroup mode={mode} title={t('auth.registration.form.state')}>
            <ProfileInfoStatesField mode={mode} />
          </FormGroup>
        )}
        {renderTextField('city')}
        <div className="rowGroup">
          {renderTextField('address')}
          {renderTextField('postal_code')}
        </div>
        <div className="submitBtn">
          <Button
            isLoading={saveFieldsPending}
            large
            primary
            type="submit"
            wide
          >
            {t('auth.registration.buttons.next')}
          </Button>
        </div>
      </Form>
      <ScSecurityMessage>
        <p className="message">{t('deposits:paymentMethodPersonalInfoText')}</p>
      </ScSecurityMessage>
    </ScProfileInfoForm>
  );
};
